// ILF Commerce v1.1 — shared, theme-agnostic commerce runtime.
//
// Ships the custom elements and services that ILF sections need to run
// identically on any theme (tested targets: Dawn 12–15 stores and Horizon),
// with ZERO theme.liquid edits and no dependency on the host theme's
// JavaScript (global.js, product-info.js, product-form.js, pubsub.js).
//
// Elements defined here (all guarded, safe to load more than once):
//   <ilf-variant-selects>  variant option state + section re-render pipeline
//   <ilf-product-info>     quantity-rules plumbing after variant/cart changes
//   <ilf-product-form>     add-to-cart form wrapper (submits through IlfCart)
//   <ilf-quantity-input>   quantity stepper
//   <ilf-deferred-media>   poster button that swaps in the real media
//   <ilf-quick-view>       product-card quick view: instant modal from a
//                          server-rendered <template>, or a fetched product page
//
// Requires assets/ilf-web-components.js — the generic UI library that owns
// <ilf-modal> (used by <ilf-quick-view> and section modals). Every component
// has exactly one home; consuming sections load both files with their own
// <script src … defer> tags (see the ilf-library-section skill).
//
// Public document-level events (the integration API for sections, elements
// and per-theme handlers — dispatch site never needs rebuilding to support a
// new theme, only a new listener):
//   'ilf:cart:added'      detail { response, sections, items, source, activeElement }
//   'ilf:cart:error'      detail { message, description, source, response }
//   'ilf:variant:changed' detail { sectionId, variantId, variant, selectedOptions }
//   'ilf:quickview:opened' detail { mode, productUrl, source }
//   'variant:change'      legacy alias of ilf:variant:changed (kept for
//                         existing external consumers; prefer the ilf: name)
//   'quantity:change'     detail { newQuantity } (from <ilf-quantity-input>)
//   'ilf:deferred-media:load' detail { element, mediaId } — bubbles from
//                         <ilf-deferred-media> right before its media is
//                         inserted; the hosting component pauses its own
//                         media on it (the runtime pauses nothing page-wide)
//
// window.IlfCart is the single deliberate global — the shared add-to-cart
// service used by ILF elements across files (ilf-product-upsell.js, future
// product cards). Theme-specific cart UI updates live in separate handlers
// at the bottom of this file: the Dawn handler ships enabled; a Horizon
// handler is added the same way when deploying to a Horizon store (listen to
// 'ilf:cart:added', replace IlfCart.sectionsProvider — nothing else changes).

(() => {
  // ---------------------------------------------------------------------
  // Internal helpers (private — never attached to window)
  // ---------------------------------------------------------------------

  const cartRoot = () => window.Shopify?.routes?.root || '/';

  // Minimal pub/sub bus, internal to ILF elements on this page. Mirrors the
  // Dawn pattern but is fully namespaced — it never touches the host theme's
  // subscribers even when the theme ships its own pubsub.js.
  const BUS_EVENTS = {
    quantityUpdate: 'ilf-quantity-update',
    variantChange: 'ilf-variant-change',
    cartUpdate: 'ilf-cart-update',
  };
  const busSubscribers = {};
  const busSubscribe = (eventName, callback) => {
    busSubscribers[eventName] = [...(busSubscribers[eventName] || []), callback];
    return () => {
      busSubscribers[eventName] = busSubscribers[eventName].filter((cb) => cb !== callback);
    };
  };
  const busPublish = (eventName, data) => {
    (busSubscribers[eventName] || []).forEach((callback) => callback(data));
  };

  const dispatchDocumentEvent = (name, detail) => {
    document.dispatchEvent(new CustomEvent(name, { detail }));
  };

  // UI strings with a portable fallback chain: element data attribute
  // (plain English rendered by the section markup) → Dawn's
  // window.variantStrings (present on Dawn stores) → English literal.
  const uiString = (el, dataKey, dawnKey, fallback) =>
    el?.dataset?.[dataKey] || window.variantStrings?.[dawnKey] || fallback;

  const getSectionInnerHTML = (html, selector) =>
    new DOMParser().parseFromString(html, 'text/html').querySelector(selector)?.innerHTML;

  // Portable equivalent of Dawn's window.pauseAllMedia for themes without one
  // (Horizon). No ILF component calls it: each pauses media within its own
  // scope only (see <ilf-product-media-gallery>). It stays defined because it
  // is a public global on the stores this file ships to.
  if (typeof window.pauseAllMedia !== 'function') {
    window.pauseAllMedia = () => {
      document.querySelectorAll('.js-youtube').forEach((video) => {
        video.contentWindow.postMessage('{"event":"command","func":"pauseVideo","args":""}', '*');
      });
      document.querySelectorAll('.js-vimeo').forEach((video) => {
        video.contentWindow.postMessage('{"method":"pause"}', '*');
      });
      document.querySelectorAll('video').forEach((video) => video.pause());
      document.querySelectorAll('product-model, ilf-product-model').forEach((model) => {
        if (model.modelViewerUI) model.modelViewerUI.pause();
      });
    };
  }

  // ---------------------------------------------------------------------
  // IlfCart — the shared add-to-cart service
  // ---------------------------------------------------------------------
  //
  // IlfCart.add(payload, options) → Promise<response>
  //   payload: FormData (a full product form) or { items: [{ id, quantity }] }
  //   options: { source } — identifier echoed in the dispatched events
  //
  // The POST always goes to /cart/add.js. Cart-UI section HTML is requested
  // in the same call via IlfCart.sectionsProvider() (replaceable per theme)
  // and comes back on response.sections for the theme handler to consume.

  if (!window.IlfCart) {
    window.IlfCart = {
      // Returns the section ids whose rendered HTML should ride along with
      // the add-to-cart response. Default: ask the Dawn cart elements.
      // Horizon deploy overrides this with the cart-items-component lookup.
      sectionsProvider: () => {
        const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        if (cart && typeof cart.getSectionsToRender === 'function') {
          return cart.getSectionsToRender().map((section) => section.id);
        }
        const sections = [];
        if (document.querySelector('cart-drawer')) sections.push('cart-drawer');
        if (document.getElementById('cart-icon-bubble')) sections.push('cart-icon-bubble');
        return sections;
      },

      add(payload, { source = 'ilf-cart' } = {}) {
        const activeElement = document.activeElement;
        const sections = this.sectionsProvider() || [];
        const config = { method: 'POST', headers: { 'X-Requested-With': 'XMLHttpRequest' } };

        if (payload instanceof FormData) {
          if (sections.length) {
            payload.append('sections', sections.join(','));
            payload.append('sections_url', window.location.pathname);
          }
          config.body = payload;
        } else {
          config.headers['Content-Type'] = 'application/json';
          config.body = JSON.stringify({
            ...payload,
            ...(sections.length && { sections: sections.join(','), sections_url: window.location.pathname }),
          });
        }

        return fetch(`${cartRoot()}cart/add.js`, config)
          .then((response) => response.json())
          .then((response) => {
            // /cart/add.js reports failures (sold out, cart limits) as a JSON
            // body carrying status + a human-readable description.
            if (response.status && response.status !== 200) {
              dispatchDocumentEvent('ilf:cart:error', {
                message: response.message,
                description: response.description || response.message,
                source,
                response,
              });
              const error = new Error(response.description || response.message || 'Could not add to cart');
              error.response = response;
              throw error;
            }

            busPublish(BUS_EVENTS.cartUpdate, { source, response });
            dispatchDocumentEvent('ilf:cart:added', {
              response,
              sections: response.sections || null,
              items: response.items || [response],
              source,
              activeElement,
            });
            return response;
          });
      },
    };
  }

  // ---------------------------------------------------------------------
  // <ilf-quantity-input>
  // ---------------------------------------------------------------------

  if (!customElements.get('ilf-quantity-input')) {
    class IlfQuantityInput extends HTMLElement {
      constructor() {
        super();
        this.input = this.querySelector('input');
        this.changeEvent = new Event('change', { bubbles: true });
        this.input.addEventListener('change', this.onInputChange.bind(this));
        this.querySelectorAll('button').forEach((button) =>
          button.addEventListener('click', this.onButtonClick.bind(this))
        );
      }

      quantityUpdateUnsubscriber = undefined;

      connectedCallback() {
        this.validateQtyRules();
        this.quantityUpdateUnsubscriber = busSubscribe(BUS_EVENTS.quantityUpdate, this.validateQtyRules.bind(this));
      }

      disconnectedCallback() {
        if (this.quantityUpdateUnsubscriber) this.quantityUpdateUnsubscriber();
      }

      onInputChange(event) {
        this.validateQtyRules();
        dispatchDocumentEvent('quantity:change', { newQuantity: event.target.value });
      }

      onButtonClick(event) {
        event.preventDefault();
        const previousValue = this.input.value;

        if (event.target.name === 'plus') {
          if (parseInt(this.input.dataset.min) > parseInt(this.input.step) && this.input.value == 0) {
            this.input.value = this.input.dataset.min;
          } else {
            this.input.stepUp();
          }
        } else {
          this.input.stepDown();
        }

        if (previousValue !== this.input.value) this.input.dispatchEvent(this.changeEvent);

        if (this.input.dataset.min === previousValue && event.target.name === 'minus') {
          this.input.value = parseInt(this.input.min);
        }
      }

      validateQtyRules() {
        const value = parseInt(this.input.value);
        if (this.input.min) {
          const buttonMinus = this.querySelector(".quantity__button[name='minus']");
          buttonMinus?.classList.toggle('disabled', value <= parseInt(this.input.min));
        }
        if (this.input.max) {
          const buttonPlus = this.querySelector(".quantity__button[name='plus']");
          buttonPlus?.classList.toggle('disabled', value >= parseInt(this.input.max));
        }
      }
    }

    customElements.define('ilf-quantity-input', IlfQuantityInput);
  }

  // ---------------------------------------------------------------------
  // <ilf-variant-selects>
  // ---------------------------------------------------------------------
  //
  // Markup contract (rendered by ilf-product-option-picker.liquid):
  // - keeps id="variant-selects-{{ section.id }}" so sibling ILF scripts
  //   (badges, option picker UI) locate it by id, independent of the tag name
  // - one <fieldset>/<select> per product option, in option order
  // - a <script type="application/json"> child with product.variants
  // - string data attributes: data-text-sold-out, data-text-unavailable,
  //   data-text-add-to-cart, data-text-unavailable-with-option

  if (!customElements.get('ilf-variant-selects')) {
    class IlfVariantSelects extends HTMLElement {
      constructor() {
        super();
        this.addEventListener('change', this.onVariantChange);
      }

      // Dawn's quick-add modal clones the product section and prefixes every
      // occurrence of the section id with "quickadd-", but it tags only ITS
      // element names with data-original-section. Derive the real section id
      // ourselves so re-render fetches keep working inside the modal.
      get renderSectionId() {
        return this.dataset.originalSection || this.dataset.section.replace(/^quickadd-/, '');
      }

      onVariantChange(event) {
        this.updateOptions();
        this.updateMasterId();
        this.updateSelectedSwatchValue(event);
        this.toggleAddButton(true, '', false);
        this.removeErrorMessage();
        this.updateVariantStatuses();

        // Public notification of the selection — fired regardless of whether
        // the selection resolves to an existing variant.
        const variantDetail = {
          sectionId: this.renderSectionId,
          variantId: this.currentVariant ? this.currentVariant.id : null,
          variant: this.currentVariant || null,
          selectedOptions: this.options || [],
        };
        dispatchDocumentEvent('ilf:variant:changed', variantDetail);
        dispatchDocumentEvent('variant:change', variantDetail); // legacy alias

        if (!this.currentVariant) {
          this.toggleAddButton(true, '', true);
          this.setUnavailable();
        } else {
          this.updateURL();
          this.updateVariantInput();
          this.renderProductInfo();
        }
      }

      updateOptions() {
        this.options = Array.from(this.querySelectorAll('select, fieldset'), (element) => {
          if (element.tagName === 'SELECT') {
            return element.value;
          }
          if (element.tagName === 'FIELDSET') {
            return Array.from(element.querySelectorAll('input')).find((radio) => radio.checked)?.value;
          }
        });
      }

      updateMasterId() {
        this.currentVariant = this.getVariantData().find((variant) => {
          return !variant.options
            .map((option, index) => {
              return this.options[index] === option;
            })
            .includes(false);
        });
      }

      updateSelectedSwatchValue({ target }) {
        const { name, value, tagName } = target;

        if (tagName === 'SELECT' && target.selectedOptions.length) {
          const swatchValue = target.selectedOptions[0].dataset.optionSwatchValue;
          const selectedDropdownSwatchValue = this.querySelector(`[data-selected-dropdown-swatch="${name}"] > .swatch`);
          if (!selectedDropdownSwatchValue) return;
          if (swatchValue) {
            selectedDropdownSwatchValue.style.setProperty('--swatch--background', swatchValue);
            selectedDropdownSwatchValue.classList.remove('swatch--unavailable');
          } else {
            selectedDropdownSwatchValue.style.setProperty('--swatch--background', 'unset');
            selectedDropdownSwatchValue.classList.add('swatch--unavailable');
          }

          selectedDropdownSwatchValue.style.setProperty(
            '--swatch-focal-point',
            target.selectedOptions[0].dataset.optionSwatchFocalPoint || 'unset'
          );
        } else if (tagName === 'INPUT' && target.type === 'radio') {
          const selectedSwatchValue = this.querySelector(`[data-selected-swatch-value="${name}"]`);
          if (selectedSwatchValue) selectedSwatchValue.innerHTML = value;
        }
      }

      updateURL() {
        if (!this.currentVariant || this.dataset.updateUrl === 'false') return;
        // Never rewrite the page URL from inside a quick-add modal — the
        // visitor is on a collection/list page, not the product page.
        if (this.dataset.section.startsWith('quickadd-')) return;
        window.history.replaceState({}, '', `${this.dataset.url}?variant=${this.currentVariant.id}`);
      }

      updateVariantInput() {
        const productForms = document.querySelectorAll(
          `#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`
        );
        productForms.forEach((productForm) => {
          const input = productForm.querySelector('input[name="id"]');
          input.value = this.currentVariant.id;
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      }

      updateVariantStatuses() {
        const selectedOptionOneVariants = this.variantData.filter(
          (variant) => this.querySelector(':checked').value === variant.option1
        );
        const inputWrappers = [...this.querySelectorAll('.product-form__input')];
        inputWrappers.forEach((option, index) => {
          if (index === 0) return;
          const optionInputs = [...option.querySelectorAll('input[type="radio"], option')];
          const previousOptionSelected = inputWrappers[index - 1].querySelector(':checked').value;
          const availableOptionInputsValue = selectedOptionOneVariants
            .filter((variant) => variant.available && variant[`option${index}`] === previousOptionSelected)
            .map((variantOption) => variantOption[`option${index + 1}`]);
          this.setInputAvailability(optionInputs, availableOptionInputsValue);
        });
      }

      setInputAvailability(elementList, availableValuesList) {
        elementList.forEach((element) => {
          const value = element.getAttribute('value');
          const availableElement = availableValuesList.includes(value);

          if (element.tagName === 'INPUT') {
            element.classList.toggle('disabled', !availableElement);
          } else if (element.tagName === 'OPTION') {
            element.innerText = availableElement
              ? value
              : uiString(this, 'textUnavailableWithOption', 'unavailable_with_option', '[value] - Unavailable').replace(
                  '[value]',
                  value
                );
          }
        });
      }

      removeErrorMessage() {
        const section = this.closest('section');
        if (!section) return;

        const productForm = section.querySelector('ilf-product-form');
        if (productForm) productForm.handleErrorMessage();
      }

      updateMedia(html) {
        // ILF gallery contract: the gallery root keeps
        // id="MediaGallery-{{ section.id }}" and exposes setActiveMedia().
        // The <ul> sync below is Dawn's stock behaviour; it no-ops safely on
        // the Swiper-based ILF gallery (no matching <ul>).
        const mediaGallerySource = document.querySelector(`[id^="MediaGallery-${this.dataset.section}"] ul`);
        const mediaGalleryDestination = html.querySelector(`[id^="MediaGallery-${this.dataset.section}"] ul`);

        const refreshSourceData = () => {
          const mediaGallerySourceItems = Array.from(mediaGallerySource.querySelectorAll('li[data-media-id]'));
          const sourceSet = new Set(mediaGallerySourceItems.map((item) => item.dataset.mediaId));
          const sourceMap = new Map(
            mediaGallerySourceItems.map((item, index) => [item.dataset.mediaId, { item, index }])
          );
          return [mediaGallerySourceItems, sourceSet, sourceMap];
        };

        if (mediaGallerySource && mediaGalleryDestination) {
          let [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
          const mediaGalleryDestinationItems = Array.from(
            mediaGalleryDestination.querySelectorAll('li[data-media-id]')
          );
          const destinationSet = new Set(mediaGalleryDestinationItems.map(({ dataset }) => dataset.mediaId));
          let shouldRefresh = false;

          // add items from new data not present in DOM
          for (let i = mediaGalleryDestinationItems.length - 1; i >= 0; i--) {
            if (!sourceSet.has(mediaGalleryDestinationItems[i].dataset.mediaId)) {
              mediaGallerySource.prepend(mediaGalleryDestinationItems[i]);
              shouldRefresh = true;
            }
          }

          // remove items from DOM not present in new data
          for (let i = 0; i < mediaGallerySourceItems.length; i++) {
            if (!destinationSet.has(mediaGallerySourceItems[i].dataset.mediaId)) {
              mediaGallerySourceItems[i].remove();
              shouldRefresh = true;
            }
          }

          // refresh
          if (shouldRefresh) [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();

          // if media galleries don't match, sort to match new data order
          mediaGalleryDestinationItems.forEach((destinationItem, destinationIndex) => {
            const sourceData = sourceMap.get(destinationItem.dataset.mediaId);

            if (sourceData && sourceData.index !== destinationIndex) {
              mediaGallerySource.insertBefore(
                sourceData.item,
                mediaGallerySource.querySelector(`li:nth-of-type(${destinationIndex + 1})`)
              );
              [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
            }
          });
        }

        if (this.currentVariant.featured_media) {
          document
            .querySelector(`[id^="MediaGallery-${this.dataset.section}"]`)
            ?.setActiveMedia?.(`${this.dataset.section}-${this.currentVariant.featured_media.id}`);
        }
      }

      renderProductInfo() {
        const requestedVariantId = this.currentVariant.id;
        const sectionId = this.renderSectionId;

        fetch(`${this.dataset.url}?variant=${requestedVariantId}&section_id=${sectionId}`)
          .then((response) => response.text())
          .then((responseText) => {
            // prevent unnecessary ui changes from abandoned selections
            if (this.currentVariant.id !== requestedVariantId) return;

            const html = new DOMParser().parseFromString(responseText, 'text/html');
            const destination = document.getElementById(`price-${this.dataset.section}`);
            const source = html.getElementById(`price-${sectionId}`);

            this.updateMedia(html);

            if (source && destination) destination.innerHTML = source.innerHTML;

            const price = document.getElementById(`price-${this.dataset.section}`);
            if (price) price.classList.remove('hidden');

            const addButtonUpdated = html.getElementById(`ProductSubmitButton-${sectionId}`);
            this.toggleAddButton(
              addButtonUpdated ? addButtonUpdated.hasAttribute('disabled') : true,
              uiString(this, 'textSoldOut', 'soldOut', 'Sold out')
            );

            busPublish(BUS_EVENTS.variantChange, {
              data: {
                sectionId,
                html,
                variant: this.currentVariant,
              },
            });
          });
      }

      toggleAddButton(disable = true, text, modifyClass = true) {
        const productForm = document.getElementById(`product-form-${this.dataset.section}`);
        if (!productForm) return;
        const addButton = productForm.querySelector('[name="add"]');
        const addButtonText = productForm.querySelector('[name="add"] > span');
        if (!addButton) return;

        if (disable) {
          addButton.setAttribute('disabled', 'disabled');
          if (text) addButtonText.textContent = text;
        } else {
          addButton.removeAttribute('disabled');
          addButtonText.textContent = uiString(this, 'textAddToCart', 'addToCart', 'Add to cart');
        }

        if (!modifyClass) return;
      }

      setUnavailable() {
        const productForm = document.getElementById(`product-form-${this.dataset.section}`);
        const price = document.getElementById(`price-${this.dataset.section}`);
        if (!productForm) return;
        const addButton = productForm.querySelector('[name="add"]');
        const addButtonText = productForm.querySelector('[name="add"] > span');

        if (!addButton) return;
        addButtonText.textContent = uiString(this, 'textUnavailable', 'unavailable', 'Unavailable');
        if (price) price.classList.add('hidden');
      }

      getVariantData() {
        this.variantData = this.variantData || JSON.parse(this.querySelector('[type="application/json"]').textContent);
        return this.variantData;
      }
    }

    customElements.define('ilf-variant-selects', IlfVariantSelects);
  }

  // ---------------------------------------------------------------------
  // <ilf-product-info>
  // ---------------------------------------------------------------------

  if (!customElements.get('ilf-product-info')) {
    class IlfProductInfo extends HTMLElement {
      constructor() {
        super();
        this.input = this.querySelector('.quantity__input');
        this.currentVariant = this.querySelector('.product-variant-id');
      }

      cartUpdateUnsubscriber = undefined;
      variantChangeUnsubscriber = undefined;

      connectedCallback() {
        if (!this.input) return;
        this.quantityForm = this.querySelector('.product-form__quantity');
        if (!this.quantityForm) return;
        this.setQuantityBoundries();
        if (!this.dataset.originalSection) {
          this.cartUpdateUnsubscriber = busSubscribe(BUS_EVENTS.cartUpdate, this.fetchQuantityRules.bind(this));
        }
        this.variantChangeUnsubscriber = busSubscribe(BUS_EVENTS.variantChange, (event) => {
          const sectionId =
            this.dataset.originalSection || this.dataset.section.replace(/^quickadd-/, '');
          if (event.data.sectionId !== sectionId) return;
          this.updateQuantityRules(event.data.sectionId, event.data.html);
          this.setQuantityBoundries();
        });
      }

      disconnectedCallback() {
        if (this.cartUpdateUnsubscriber) this.cartUpdateUnsubscriber();
        if (this.variantChangeUnsubscriber) this.variantChangeUnsubscriber();
      }

      setQuantityBoundries() {
        const data = {
          cartQuantity: this.input.dataset.cartQuantity ? parseInt(this.input.dataset.cartQuantity) : 0,
          min: this.input.dataset.min ? parseInt(this.input.dataset.min) : 1,
          max: this.input.dataset.max ? parseInt(this.input.dataset.max) : null,
          step: this.input.step ? parseInt(this.input.step) : 1,
        };

        let min = data.min;
        const max = data.max === null ? data.max : data.max - data.cartQuantity;
        if (max !== null) min = Math.min(min, max);
        if (data.cartQuantity >= data.min) min = Math.min(min, data.step);
        this.input.min = min;

        if (max) {
          this.input.max = max;
        } else {
          this.input.removeAttribute('max');
        }
        this.input.value = min;
        busPublish(BUS_EVENTS.quantityUpdate, undefined);
      }

      fetchQuantityRules() {
        if (!this.currentVariant || !this.currentVariant.value) return;
        this.querySelector('.quantity__rules-cart .loading__spinner')?.classList.remove('hidden');
        fetch(`${this.dataset.url}?variant=${this.currentVariant.value}&section_id=${this.dataset.section}`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            this.updateQuantityRules(this.dataset.section, html);
            this.setQuantityBoundries();
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            this.querySelector('.quantity__rules-cart .loading__spinner')?.classList.add('hidden');
          });
      }

      updateQuantityRules(sectionId, html) {
        const quantityFormUpdated = html.getElementById(`Quantity-Form-${sectionId}`);
        if (!quantityFormUpdated) return;
        const selectors = ['.quantity__input', '.quantity__rules', '.quantity__label'];
        for (let selector of selectors) {
          const current = this.quantityForm.querySelector(selector);
          const updated = quantityFormUpdated.querySelector(selector);
          if (!current || !updated) continue;
          if (selector === '.quantity__input') {
            const attributes = ['data-cart-quantity', 'data-min', 'data-max', 'step'];
            for (let attribute of attributes) {
              const valueUpdated = updated.getAttribute(attribute);
              if (valueUpdated !== null) {
                current.setAttribute(attribute, valueUpdated);
              } else {
                current.removeAttribute(attribute);
              }
            }
          } else {
            current.innerHTML = updated.innerHTML;
          }
        }
      }
    }

    customElements.define('ilf-product-info', IlfProductInfo);
  }

  // ---------------------------------------------------------------------
  // <ilf-product-form>
  // ---------------------------------------------------------------------

  if (!customElements.get('ilf-product-form')) {
    class IlfProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.form.querySelector('[name=id]').disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.submitButton = this.querySelector('[type="submit"]');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner')?.classList.remove('hidden');

        const formData = new FormData(this.form);

        window.IlfCart.add(formData, { source: 'ilf-product-form' })
          .then(() => {
            this.error = false;
          })
          .catch((error) => {
            if (error.response) {
              // Cart-level rejection (sold out, limits) — show it on the form.
              this.handleErrorMessage(error.response.description || error.response.message);

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButton.querySelector('span').classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
            } else {
              console.error(error);
            }
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner')?.classList.add('hidden');
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage =
          this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }
    }

    customElements.define('ilf-product-form', IlfProductForm);
  }

  // ---------------------------------------------------------------------
  // <ilf-deferred-media>
  // ---------------------------------------------------------------------
  // Poster button that swaps itself for the real media (video, external
  // video iframe, model-viewer) from a <template> on first click. It never
  // pauses other media itself: it dispatches 'ilf:deferred-media:load'
  // (bubbling) so the hosting component can pause within its own scope.
  // <ilf-product-model> (assets/ilf-product-model.js) extends this class.

  if (!customElements.get('ilf-deferred-media')) {
    class IlfDeferredMedia extends HTMLElement {
      constructor() {
        super();
        const poster = this.querySelector('[id^="Deferred-Poster-"]');
        if (!poster) return;
        poster.addEventListener('click', this.loadContent.bind(this));
      }

      loadContent(focus = true) {
        this.dispatchEvent(
          new CustomEvent('ilf:deferred-media:load', {
            bubbles: true,
            detail: { element: this, mediaId: this.dataset.mediaId || null },
          })
        );
        if (!this.getAttribute('loaded')) {
          const content = document.createElement('div');
          content.appendChild(this.querySelector('template').content.firstElementChild.cloneNode(true));

          this.setAttribute('loaded', true);
          const deferredElement = this.appendChild(content.querySelector('video, model-viewer, iframe'));
          if (focus) deferredElement.focus();
          if (deferredElement.nodeName == 'VIDEO' && deferredElement.getAttribute('autoplay')) {
            // force autoplay for safari
            deferredElement.play();
          }
        }
      }
    }

    customElements.define('ilf-deferred-media', IlfDeferredMedia);
  }

  // <ilf-modal> is owned by assets/ilf-web-components.js (generic UI
  // library) — one home per component. <ilf-quick-view> below depends on
  // it; consuming sections load both files.

  // ---------------------------------------------------------------------
  // <ilf-quick-view>
  // ---------------------------------------------------------------------
  // Product-card quick view. Wraps the card's trigger button and provides
  // the modal content in one of two modes (data-mode):
  //   'template' — clones the element's own <template>: server-rendered
  //                option selects + <ilf-product-form>; opens instantly,
  //                works for any product regardless of its template
  //   'fetch'    — fetches data-product-url and shows the product page's
  //                main product section ([id^="MainProduct-"]) with its root
  //                element (scope + color-scheme classes) preserved; ids get
  //                the 'quickadd-' prefix, and data-original-section /
  //                data-update-url are set the way Dawn's quick-add does, so
  //                variant re-renders target the real section id whether the
  //                section runs on Dawn's product-info or our elements
  // All instances share one lazily created <ilf-modal id="IlfQuickViewModal">.
  //
  // Expected markup (see snippets/ilf-product-card.liquid):
  //   <ilf-quick-view data-mode="template"
  //     data-text-add-to-cart="Add to cart" data-text-sold-out="Sold out"
  //     data-text-unavailable="Unavailable">
  //     <button type="button" data-quick-view-open>…</button>
  //     <template>
  //       …selects[data-quick-view-option] + [data-quick-view-price] +
  //       <ilf-product-form> + <script type="application/json"
  //       data-quick-view-variants>[{ id, available, options, price,
  //       compare_at_price }]</script>…
  //     </template>
  //   </ilf-quick-view>

  if (!customElements.get('ilf-quick-view')) {
    const fetchedPages = new Map();

    // Any successful add to cart closes the shared quick-view modal.
    document.addEventListener('ilf:cart:added', () => {
      const modal = document.getElementById('IlfQuickViewModal');
      if (modal?.isOpen) modal.closeModal();
    });

    class IlfQuickView extends HTMLElement {
      connectedCallback() {
        if (this.initialised) return;
        this.initialised = true;

        this.button = this.querySelector('[data-quick-view-open]');
        this.button?.addEventListener('click', this.open.bind(this));
      }

      get modal() {
        let modal = document.getElementById('IlfQuickViewModal');
        if (!modal) {
          modal = document.createElement('ilf-modal');
          modal.id = 'IlfQuickViewModal';
          modal.classList.add('ilf-modal', 'ilf-quick-view-modal');
          modal.innerHTML = `
            <div class="ilf-modal-content-wrapper" role="dialog" aria-modal="true" aria-label="Choose options">
              <button type="button" class="ilf-modal-close" aria-label="Close">&times;</button>
              <div class="ilf-quick-view-content"></div>
            </div>`;
          document.body.appendChild(modal);
        }
        return modal;
      }

      async open() {
        const modal = this.modal;
        const content = modal.querySelector('.ilf-quick-view-content');
        content.innerHTML = '';
        modal.classList.toggle('ilf-quick-view-modal--full', this.dataset.mode === 'fetch');

        if (this.dataset.mode === 'fetch') {
          this.button.classList.add('loading');
          this.button.querySelector('.loading__spinner')?.classList.remove('hidden');
          try {
            content.innerHTML = await this.fetchProductSection();
            this.prepareFetchedSection(content);
            this.activateScripts(content);
          } catch (error) {
            console.error('ilf-quick-view:', error);
            // Graceful degradation: hand the shopper the real product page.
            if (this.dataset.productUrl) window.location.href = this.dataset.productUrl;
            return;
          } finally {
            this.button.classList.remove('loading');
            this.button.querySelector('.loading__spinner')?.classList.add('hidden');
          }
        } else {
          const template = this.querySelector('template');
          if (!template) return;
          content.appendChild(template.content.cloneNode(true));
          this.wireOptionSelects(content);
        }

        // The modal lives under <body>, outside the section's DOM scope —
        // carry the section's quick-view tokens over so they still apply.
        ['--ilf-quick-view-bg', '--ilf-quick-view-fg', '--ilf-quick-view-button-bg', '--ilf-quick-view-button-fg'].forEach((property) => {
          const value = getComputedStyle(this).getPropertyValue(property);
          if (value) modal.style.setProperty(property, value);
        });

        modal.openModal(this.button);
        dispatchDocumentEvent('ilf:quickview:opened', {
          mode: this.dataset.mode || 'template',
          productUrl: this.dataset.productUrl || null,
          source: 'ilf-quick-view',
        });
      }

      async fetchProductSection() {
        const url = this.dataset.productUrl;
        if (!fetchedPages.has(url)) {
          const response = await fetch(url);
          if (!response.ok) throw new Error(`quick view fetch failed: ${response.status}`);
          fetchedPages.set(url, await response.text());
        }
        const doc = new DOMParser().parseFromString(fetchedPages.get(url), 'text/html');
        const productElement = doc.querySelector('[id^="MainProduct-"]');
        if (!productElement) throw new Error('quick view: no [id^="MainProduct-"] section on the product page');
        const sectionId = productElement.id.replace('MainProduct-', '');
        // outerHTML keeps the section's ROOT element: it carries the scope
        // class and color-scheme classes that the section's scoped CSS and
        // color variables hang off — dropping it (Dawn's own quick-add does)
        // is what makes the modal render differently from the product page.
        return productElement.outerHTML.replaceAll(sectionId, `quickadd-${sectionId}`);
      }

      // Dawn-compatible re-render wiring for the fetched section. The id
      // prefixing renames data-section to 'quickadd-<id>', but variant
      // changes re-render through the Section Rendering API, which only
      // knows the REAL section id — Dawn's product-info (and our
      // <ilf-variant-selects>) read it from data-original-section, exactly
      // like Dawn's own quick-add sets it. Without it every variant switch
      // fetches a non-existent section and renders as a false "Sold out".
      // data-update-url="false" keeps modal interactions off the page URL.
      prepareFetchedSection(root) {
        root.querySelectorAll('[data-section]').forEach((element) => {
          element.dataset.originalSection = element.dataset.section.replace(/^quickadd-/, '');
          element.dataset.updateUrl = 'false';
        });
      }

      // innerHTML-injected <script> tags are inert — replace them with live
      // clones so the fetched section's behaviour initialises.
      activateScripts(root) {
        root.querySelectorAll('script').forEach((oldScript) => {
          const script = document.createElement('script');
          [...oldScript.attributes].forEach((attr) => script.setAttribute(attr.name, attr.value));
          script.textContent = oldScript.textContent;
          oldScript.replaceWith(script);
        });
      }

      // Template mode: option selects resolve the matching variant locally
      // from the embedded variants JSON — no re-render round trip.
      wireOptionSelects(content) {
        const variantsJson = content.querySelector('[data-quick-view-variants]');
        const form = content.querySelector('form');
        if (!variantsJson || !form) return;

        const variants = JSON.parse(variantsJson.textContent);
        const selects = [...content.querySelectorAll('[data-quick-view-option]')];
        const priceEl = content.querySelector('[data-quick-view-price]');
        const compareEl = content.querySelector('[data-quick-view-compare-price]');
        const idInput = form.querySelector('[name=id]');
        const submit = form.querySelector('[type="submit"]');
        const label = submit?.querySelector('span');

        const update = () => {
          const selected = selects.map((select) => select.value);
          const variant = variants.find((v) => v.options.every((value, index) => value === selected[index]));
          const available = !!variant && variant.available;

          idInput.disabled = !variant;
          if (variant) idInput.value = variant.id;
          if (priceEl && variant) priceEl.textContent = variant.price;
          if (compareEl) {
            compareEl.textContent = variant?.compare_at_price || '';
            compareEl.hidden = !variant?.compare_at_price;
          }
          if (submit) {
            submit.toggleAttribute('disabled', !available);
            if (label) {
              if (!variant) label.textContent = uiString(this, 'textUnavailable', 'unavailable', 'Unavailable');
              else if (!available) label.textContent = uiString(this, 'textSoldOut', 'soldOut', 'Sold out');
              else label.textContent = uiString(this, 'textAddToCart', 'addToCart', 'Add to cart');
            }
          }
        };

        selects.forEach((select) => select.addEventListener('change', update));
        update();
      }
    }

    customElements.define('ilf-quick-view', IlfQuickView);
  }

  // ---------------------------------------------------------------------
  // Theme cart-UI handlers
  // ---------------------------------------------------------------------
  // One listener per theme family. Only the handler whose UI actually exists
  // on the page does anything, so all handlers can ship together.

  // Dawn (all versions): let the theme's cart element render the response if
  // it knows how; otherwise swap the returned section HTML manually.
  document.addEventListener('ilf:cart:added', (event) => {
    const { response, sections, activeElement } = event.detail;
    const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
    if (!cart) return;

    if (typeof cart.setActiveElement === 'function' && activeElement) {
      cart.setActiveElement(activeElement);
    }

    if (typeof cart.renderContents === 'function' && sections) {
      cart.renderContents(response);
    } else if (sections) {
      const cartDrawerHTML = sections['cart-drawer'];
      const drawerInner = document.getElementById('CartDrawer');
      if (cartDrawerHTML && drawerInner) {
        const newContent = getSectionInnerHTML(cartDrawerHTML, '#CartDrawer');
        if (newContent) drawerInner.innerHTML = newContent;
      }

      const cartIconHTML = sections['cart-icon-bubble'];
      const iconContainer = document.getElementById('cart-icon-bubble');
      if (cartIconHTML && iconContainer) {
        const newContent = getSectionInnerHTML(cartIconHTML, '#shopify-section-cart-icon-bubble');
        if (newContent) iconContainer.innerHTML = newContent;
      }
    }

    if (cart.classList.contains('is-empty')) cart.classList.remove('is-empty');
  });

  // Horizon: added at deploy time to the Horizon store. Contract (verified
  // against Shopify/horizon): dispatch 'shopify:cart:lines-update' on
  // document with { action: 'add', lines, promise } assigned on the event
  // object, and override IlfCart.sectionsProvider with
  // [...document.querySelectorAll('cart-items-component')].map(el => el.dataset.sectionId).
})();
